import { Query, QueryClient } from "@tanstack/react-query";

import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { setQuoteRequestLineQueryData } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { JobStatusSchema } from "shared";

export const invalidatePart = (queryClient: QueryClient, { partId }: { partId: string }) => {
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: queryKeys.parts.detail(partId) }),
    invalidateQuoteRequestLinesForPart(queryClient, partId),
  ];

  return Promise.all(invalidations);
};

const invalidateQuoteRequestLinesForPart = (queryClient: QueryClient, partId: string) =>
  queryClient.invalidateQueries({
    predicate: (query) => isQrlQueryFromPart(query, partId),
  });

export const updateQuoteRequestLinesForPart = (
  queryClient: QueryClient,
  partId: string,
  partUpdate: Partial<PartDto>,
) => {
  const queries = queryClient
    .getQueryCache()
    .findAll({ predicate: (query) => isQrlQueryFromPart(query, partId) });

  queries.forEach((query) => {
    const id = query.queryKey.at(-1) as string;
    setQuoteRequestLineQueryData(queryClient, id, (old) =>
      old ? { ...old, part: { ...old.part, ...partUpdate } } : old,
    );
  });
};

export const markQuoteRequestLinesPredictingForPart = (
  queryClient: QueryClient,
  partId: string,
): QuoteRequestLineDto[] => {
  const queries = queryClient
    .getQueryCache()
    .findAll({ predicate: (query) => isQrlQueryFromPart(query, partId) });

  const previousQuoteRequestLines: QuoteRequestLineDto[] = [];

  queries.forEach((query) => {
    const id = query.queryKey.at(-1) as string;
    const previousQuoteRequestLine = query.state.data as QuoteRequestLineDto | undefined;

    if (previousQuoteRequestLine) {
      previousQuoteRequestLines.push(previousQuoteRequestLine);
    }

    setQuoteRequestLineQueryData(queryClient, id, (old) =>
      old ? { ...old, predictionStatus: JobStatusSchema.enum.STANDBY } : old,
    );
  });

  return previousQuoteRequestLines;
};

const isQrlQueryFromPart = (query: Query, partId: string) => {
  const randomString = "___";
  const keyWithRandomId = queryKeys.quoteRequestLines.detail(randomString);
  return (
    query.queryKey.length === keyWithRandomId.length &&
    query.queryKey.every(
      (key, index) => key === keyWithRandomId[index] || keyWithRandomId[index] === randomString,
    ) &&
    (query.state.data as { partId?: string } | undefined)?.partId === partId
  );
};
